/**
 * Shared Chart.js renderer for price scenario charts.
 * Used by product-informe page and latest-reports cards.
 *
 * Usage:
 *   window.InformeChart.render(canvas, options)
 *
 * Options:
 *   currency    - string, e.g. '$', '€'
 *   actual      - number, current price
 *   reportDate  - string, 'YYYY-MM-DD' or 'YYYY-MM'
 *   base        - { m6, m12, m24 } base scenario prices
 *   bear        - { m6, m12, m24 } pessimistic prices (optional)
 *   bull        - { m6, m12, m24 } optimistic prices (optional)
 *   evolution   - [{ date, price }] real price evolution (optional)
 *   analystTargets - [{ date, target_mean, target_median, target_high, target_low,
 *                      num_analysts, upside_pct, recommendation, currency, current_price }]
 *                   analyst consensus snapshots (optional)
 *   showLegend  - boolean (default true)
 *   compact     - boolean, smaller for cards (default false)
 */
(function() {
  'use strict';

  var monthNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

  function fmtDate(d) {
    return monthNames[d.getMonth()] + ' ' + d.getFullYear();
  }

  function fmtTimestamp(ts) {
    var d = new Date(ts);
    return monthNames[d.getMonth()] + ' ' + d.getFullYear();
  }

  function addMonths(d, m) {
    var r = new Date(d);
    r.setMonth(r.getMonth() + m);
    return r;
  }

  function fmtPrice(v, currency) {
    if (v >= 1000) return currency + v.toFixed(0);
    return currency + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }

  function v2d(v) { return v > 0 ? v : null; }

  function parseReportDate(str) {
    if (!str) return new Date();
    if (str.split('-').length === 2) str += '-01';
    var d = new Date(str);
    return isNaN(d.getTime()) ? new Date() : d;
  }

  function render(canvas, opts) {
    if (!canvas || typeof Chart === 'undefined') return null;

    var currency = opts.currency || '';
    var actual = opts.actual || 0;
    if (actual <= 0) return null;

    var reportDate = parseReportDate(opts.reportDate);
    var base = opts.base || {};
    var bear = opts.bear || {};
    var bull = opts.bull || {};
    var showLegend = opts.showLegend !== false;
    var compact = opts.compact || false;

    /* Scenario milestone timestamps */
    var t0  = reportDate.getTime();
    var t6  = addMonths(reportDate, 6).getTime();
    var t12 = addMonths(reportDate, 12).getTime();
    var t24 = addMonths(reportDate, 24).getTime();

    /* Scenario data as {x, y} points */
    var baseData = [
      { x: t0, y: actual },
      { x: t6, y: v2d(base.m6) },
      { x: t12, y: v2d(base.m12) },
      { x: t24, y: v2d(base.m24) }
    ];
    var bearData = [
      { x: t0, y: actual },
      { x: t6, y: v2d(bear.m6) },
      { x: t12, y: v2d(bear.m12) },
      { x: t24, y: v2d(bear.m24) }
    ];
    var bullData = [
      { x: t0, y: actual },
      { x: t6, y: v2d(bull.m6) },
      { x: t12, y: v2d(bull.m12) },
      { x: t24, y: v2d(bull.m24) }
    ];

    var hasBase = base.m6 > 0 || base.m12 > 0 || base.m24 > 0;
    var hasBear = bear.m6 > 0 || bear.m12 > 0 || bear.m24 > 0;
    var hasBull = bull.m6 > 0 || bull.m12 > 0 || bull.m24 > 0;

    var baseColor = '#3b82f6';
    var bearColor = '#dc2626';
    var bullColor = '#16a34a';

    /* Determine which scenario is validated (solid line) */
    var validatedTesis = (opts.validatedTesis || 'Base').toLowerCase();
    var isBullValidated = validatedTesis === 'optimista' || validatedTesis === 'bull';
    var isBearValidated = validatedTesis === 'pesimista' || validatedTesis === 'bear';
    var isBaseValidated = !isBullValidated && !isBearValidated;

    var datasets = [];

    /* 1. Price evolution (first in legend) */
    var evolution = opts.evolution || [];
    var evoDataset = null;
    if (evolution.length > 0) {
      var reportTime = reportDate.getTime();
      var evoPoints = [];
      var evoMeta = []; /* per-point metadata: { isPromising, isFailing } */

      evolution.forEach(function(sp) {
        var d = new Date(sp.date);
        if (isNaN(d.getTime())) return;
        var price = parseFloat(String(sp.price || '').replace(/[^0-9.,\-]/g, '').replace(/,/g, '')) || 0;
        if (price <= 0) return;
        if (d.getTime() < reportTime) return;
        evoPoints.push({ x: d.getTime(), y: price });
        evoMeta.push({
          isPromising: sp.is_promising === true || sp.is_promising === 'true',
          isFailing: sp.is_failing === true || sp.is_failing === 'true',
          isPromisingStop: sp.is_promising_stop === true || sp.is_promising_stop === 'true'
        });
      });
      evoPoints.sort(function(a, b) { return a.x - b.x; });
      /* Sort meta in sync with points */
      var evoIndices = evolution.map(function(sp, i) {
        var d = new Date(sp.date);
        return { time: isNaN(d.getTime()) ? 0 : d.getTime(), idx: i };
      }).filter(function(e) { return e.time >= reportTime; }).sort(function(a, b) { return a.time - b.time; });
      var evoMetaSorted = evoIndices.map(function(e) { return evoMeta[e.idx] || { isPromising: false, isFailing: false }; });

      if (evoPoints.length === 0 || evoPoints[0].x > t0) {
        evoPoints.unshift({ x: t0, y: actual });
        evoMetaSorted.unshift({ isPromising: false, isFailing: false, isPromisingStop: false });
      }

      if (evoPoints.length > 0) {
        var defaultR = compact ? 4 : 5;
        var highlightR = compact ? 6 : 8;
        var ptRadius = [], ptBgColor = [], ptBorderColor = [], ptBorderWidth = [], ptHoverRadius = [];

        for (var ei = 0; ei < evoPoints.length; ei++) {
          var meta = evoMetaSorted[ei] || {};
          if (meta.isPromisingStop) {
            ptRadius.push(highlightR);
            ptBgColor.push('#dc2626');
            ptBorderColor.push('#16a34a');
            ptBorderWidth.push(3);
            ptHoverRadius.push(highlightR + 2);
          } else if (meta.isPromising) {
            ptRadius.push(highlightR);
            ptBgColor.push('#fff');
            ptBorderColor.push('#16a34a');
            ptBorderWidth.push(3);
            ptHoverRadius.push(highlightR + 2);
          } else {
            ptRadius.push(defaultR);
            ptBgColor.push('#5a4a3f');
            ptBorderColor.push('#fff');
            ptBorderWidth.push(2);
            ptHoverRadius.push(compact ? 5 : 8);
          }
        }

        evoDataset = {
          label: 'Precio Real',
          data: evoPoints,
          _evoMeta: evoMetaSorted,
          borderColor: '#5a4a3f',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.1,
          pointRadius: ptRadius,
          pointHoverRadius: ptHoverRadius,
          pointBackgroundColor: ptBgColor,
          pointBorderColor: ptBorderColor,
          pointBorderWidth: ptBorderWidth,
          borderWidth: compact ? 2.5 : 3,
          spanGaps: true,
          order: 0
        };
        datasets.push(evoDataset);
      }
    }

    /* 2. SR scenario lines (legend order: Optimista, Base, Pesimista) */
    var bullIdx = -1, baseIdx = -1, bearIdx = -1;

    if (hasBull) {
      bullIdx = datasets.length;
      datasets.push({
        label: 'Optimista SR',
        _validated: isBullValidated,
        data: bullData.slice(),
        borderColor: bullColor,
        borderWidth: isBullValidated ? (compact ? 2 : 2.5) : (compact ? 1 : 1.5),
        borderDash: isBullValidated ? [] : [4, 3],
        pointRadius: isBullValidated ? (compact ? 3 : 5) : (compact ? 2 : 3),
        pointHoverRadius: isBullValidated ? (compact ? 4 : 7) : (compact ? 3 : 5),
        pointBackgroundColor: bullColor,
        tension: 0.3,
        spanGaps: true,
        fill: false,
        order: isBullValidated ? 1 : 2
      });
    }

    if (hasBase) {
      baseIdx = datasets.length;
      datasets.push({
        label: 'Base SR',
        _validated: isBaseValidated,
        data: baseData.slice(),
        borderColor: baseColor,
        borderWidth: isBaseValidated ? (compact ? 2 : 2.5) : (compact ? 1 : 1.5),
        borderDash: isBaseValidated ? [] : [4, 3],
        pointRadius: isBaseValidated ? (compact ? 3 : 5) : (compact ? 2 : 3),
        pointHoverRadius: isBaseValidated ? (compact ? 4 : 7) : (compact ? 3 : 5),
        pointBackgroundColor: baseColor,
        tension: 0.3,
        spanGaps: true,
        fill: false,
        order: isBaseValidated ? 1 : 2
      });
    }

    if (hasBear) {
      bearIdx = datasets.length;
      datasets.push({
        label: 'Pesimista SR',
        _validated: isBearValidated,
        data: bearData.slice(),
        borderColor: bearColor,
        borderWidth: isBearValidated ? (compact ? 2 : 2.5) : (compact ? 1 : 1.5),
        borderDash: isBearValidated ? [] : [4, 3],
        pointRadius: isBearValidated ? (compact ? 3 : 5) : (compact ? 2 : 3),
        pointHoverRadius: isBearValidated ? (compact ? 4 : 7) : (compact ? 3 : 5),
        pointBackgroundColor: bearColor,
        tension: 0.3,
        spanGaps: true,
        fill: false,
        order: isBearValidated ? 1 : 2
      });
    }

    /* 3. Fill zones (hidden from legend) */
    if (hasBull && hasBase && bullIdx >= 0 && baseIdx >= 0) {
      datasets.push({
        label: '_fillBullBase',
        data: bullData.slice(),
        borderColor: 'transparent',
        borderWidth: 0,
        pointRadius: 0,
        pointHitRadius: 0,
        tension: 0.3,
        spanGaps: true,
        fill: { target: baseIdx, above: 'rgba(22, 163, 74, 0.12)', below: 'transparent' },
        order: 3
      });
    }

    if (hasBear && hasBase && bearIdx >= 0 && baseIdx >= 0) {
      datasets.push({
        label: '_fillBaseBear',
        data: baseData.slice(),
        borderColor: 'transparent',
        borderWidth: 0,
        pointRadius: 0,
        pointHitRadius: 0,
        tension: 0.3,
        spanGaps: true,
        fill: { target: bearIdx, above: 'rgba(220, 38, 38, 0.10)', below: 'transparent' },
        order: 3
      });
    }

    /* Analyst consensus: single line from t0 (actual) → target_mean at +12m from snapshot date */
    var analystTargets = opts.analystTargets || [];
    var consensusColor = '#e67e22';
    var consensusEndX = 0;

    if (analystTargets.length > 0) {
      function parseP(s) {
        return parseFloat(String(s || '').replace(/[^0-9.,\-]/g, '').replace(/,/g, '')) || 0;
      }

      /* Use the most recent snapshot */
      var latest = null;
      analystTargets.forEach(function(raw) {
        var d = new Date(raw.date);
        if (isNaN(d.getTime())) return;
        var mean = parseP(raw.target_mean);
        if (mean <= 0) return;
        if (!latest || d.getTime() > latest.timestamp) {
          latest = {
            date: d,
            timestamp: d.getTime(),
            mean: mean,
            high: parseP(raw.target_high),
            low: parseP(raw.target_low),
            currentPrice: parseP(raw.current_price),
            numAnalysts: parseInt(raw.num_analysts) || 0,
            upsidePct: raw.upside_pct || '',
            recommendation: raw.recommendation || ''
          };
        }
      });

      if (latest && latest.mean > 0) {
        var targetX = addMonths(latest.date, 12).getTime();
        consensusEndX = targetX;

        datasets.push({
          label: 'Consenso',
          data: [{ x: t0, y: actual }, { x: targetX, y: latest.mean }],
          borderColor: consensusColor,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0,
          pointRadius: compact ? 3 : 4,
          pointHoverRadius: compact ? 4 : 6,
          pointBackgroundColor: consensusColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          borderWidth: compact ? 1.5 : 2,
          borderDash: [6, 4],
          spanGaps: true,
          order: 1,
          _consensusMeta: latest
        });
      }
    }

    /* Compute nice min/max for the X axis */
    var xMin = t0;
    var xMax = t24;

    /* Expand xMax if consensus projection exceeds current range */
    if (consensusEndX > xMax && consensusEndX <= addMonths(reportDate, 30).getTime()) {
      xMax = consensusEndX;
    }

    /* Plugin: underline validated legend items */
    var underlinePlugin = {
      id: 'legendUnderline',
      afterDraw: function(chart) {
        var legend = chart.legend;
        if (!legend || !legend.legendItems) return;
        var ctx = chart.ctx;
        legend.legendItems.forEach(function(item, i) {
          var ds = chart.data.datasets[item.datasetIndex];
          if (!ds || !ds._validated) return;
          /* Find the legend hit box for this item */
          var hitBox = legend.legendHitBoxes[i];
          if (!hitBox) return;
          var y = hitBox.top + hitBox.height + 1;
          var x = hitBox.left;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + hitBox.width, y);
          ctx.lineWidth = 2;
          ctx.strokeStyle = ds.borderColor;
          ctx.stroke();
          ctx.restore();
        });
      }
    };

    /* Create chart */
    return new Chart(canvas, {
      type: 'line',
      data: { datasets: datasets },
      plugins: [underlinePlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: {
            display: showLegend,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: compact ? 8 : 16,
              font: { size: compact ? 10 : 12, family: 'inherit' },
              filter: function(item) { return item.text.charAt(0) !== '_'; },
              generateLabels: function(chart) {
                var defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                var visible = defaultLabels.filter(function(l) { return l.text.charAt(0) !== '_'; });
                /* Force order: Precio Real, Optimista SR, Base SR, Pesimista SR, Consenso */
                var order = ['Precio Real', 'Optimista SR', 'Base SR', 'Pesimista SR', 'Consenso'];
                visible.sort(function(a, b) {
                  var ia = order.indexOf(a.text); if (ia < 0) ia = 99;
                  var ib = order.indexOf(b.text); if (ib < 0) ib = 99;
                  return ia - ib;
                });
                return visible.map(function(l) {
                  var ds = chart.data.datasets[l.datasetIndex];
                  if (ds && ds._validated) {
                    l.font = { weight: 'bold', size: compact ? 11 : 13 };
                  }
                  return l;
                });
              }
            },
          },
          tooltip: {
            backgroundColor: 'rgba(26, 26, 46, 0.95)',
            titleFont: { size: compact ? 11 : 13, weight: '600' },
            bodyFont: { size: compact ? 10 : 12 },
            footerFont: { size: compact ? 10 : 12, weight: '700' },
            footerColor: '#fff',
            padding: compact ? 8 : 14,
            cornerRadius: 10,
            boxPadding: 4,
            filter: function(item) { return item.dataset.label.charAt(0) !== '_'; },
            callbacks: {
              title: function(items) {
                if (!items.length) return '';
                return fmtTimestamp(items[0].parsed.x);
              },
              afterBody: function(items) {
                /* Promising / Failing badge line — prominent, below all labels */
                for (var i = 0; i < items.length; i++) {
                  var ds = items[i].dataset;
                  if (ds.label !== 'Precio Real' || !ds._evoMeta) continue;
                  var ptMeta = ds._evoMeta[items[i].dataIndex];
                  if (!ptMeta) continue;
                  var lines = [];
                  if (ptMeta.isPromisingStop) {
                    lines.push('');
                    lines.push('\u25A0 Salida del ranking');
                    lines.push('  Posici\u00f3n cerrada a este precio');
                  } else if (ptMeta.isPromising) {
                    lines.push('');
                    lines.push('\u25B2 Empresa seleccionada en el ranking');
                    lines.push('  Oportunidad identificada por la IA');
                  }
                  if (lines.length > 0) return lines;
                }
                return [];
              },
              afterBodyColor: function(ctx) {
                var ds = ctx.dataset;
                if (ds && ds.label === 'Precio Real' && ds._evoMeta) {
                  var ptMeta = ds._evoMeta[ctx.dataIndex];
                  if (ptMeta && ptMeta.isPromisingStop) return '#f59e0b';
                  if (ptMeta && ptMeta.isPromising) return '#4ade80';
                }
                return 'rgba(255,255,255,0.7)';
              },
              label: function(ctx) {
                if (ctx.dataset.label.charAt(0) === '_') return null;
                var v = ctx.parsed.y;
                if (v == null) return null;
                var pct = ((v - actual) / actual * 100);
                var sign = pct >= 0 ? '+' : '';
                var line = ctx.dataset.label + ': ' + fmtPrice(v, currency) + ' (' + sign + pct.toFixed(1) + '%)';

                if (ctx.dataset.label === 'Consenso' && ctx.dataset._consensusMeta) {
                  var meta = ctx.dataset._consensusMeta;
                  var lines = [line];
                  if (meta.numAnalysts > 0) {
                    lines.push('  Analistas: ' + meta.numAnalysts);
                  }
                  if (meta.recommendation) {
                    var rec = meta.recommendation.charAt(0).toUpperCase() + meta.recommendation.slice(1);
                    lines.push('  Recomendación: ' + rec);
                  }
                  if (meta.upsidePct) {
                    lines.push('  Potencial: ' + meta.upsidePct);
                  }
                  if (meta.high > 0 && meta.low > 0) {
                    lines.push('  Rango: ' + fmtPrice(meta.low, currency) + ' – ' + fmtPrice(meta.high, currency));
                  }
                  return lines;
                }

                return line;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: xMin,
            max: xMax,
            grid: { display: false },
            ticks: {
              font: { size: compact ? 9 : 11 },
              color: '#636e72',
              maxRotation: compact ? 45 : 0,
              callback: function(value) {
                return fmtTimestamp(value);
              },
              autoSkip: true,
              maxTicksLimit: compact ? 4 : 6
            }
          },
          y: {
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: {
              font: { size: compact ? 9 : 11 },
              color: '#636e72',
              callback: function(v) { return fmtPrice(v, currency); }
            }
          }
        }
      }
    });
  }

  window.InformeChart = { render: render };
})();
